import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import LinkButton from "common/components/LinkButton";
import Icon from "common/components/Icon";
import { StatusCode } from "common/fetch/types";
import { PromotionKind } from "common/types/promotions";

import { urls } from "client/routes";
import { ProductListSource } from "client/analytics/types";
import { useProductsViewOnVisible, useViewItemListOnVisible } from "client/analytics/utils";
import Markdown from "client/common/components/content/Markdown";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import { useBreakpoints } from "client/common/hooks";
import Banner from "client/common/components/content/Banner";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import FetchErrorContainer from "client/common/components/errors/FetchErrorContainer";

import {
  useIncentiveProductsQuery,
  useIncentiveQuery,
  useRebateProductsQuery,
  useRebateQuery,
} from "../hooks";
import ProductCard from "./ProductCard";
import ProductCardMobile from "./ProductCardMobile";

import styles from "../styles/PromotionDrawer.module.less";

export type PromotionDrawerProps = {
  kind: PromotionKind;
  id: number;
  shouldDisplayItems?: boolean;
  onNavigate?: () => void;
};

const MAX_PRODUCTS = 8;

const PromotionDrawer = ({
  kind,
  id,
  shouldDisplayItems = true,
  onNavigate,
}: PromotionDrawerProps) => {
  const { close } = useCurrentDrawer();
  const { isPhoneAndSmaller } = useBreakpoints();
  const isRebate = kind === PromotionKind.Rebate;
  const listSource = isRebate ? ProductListSource.RebateDrawer : ProductListSource.IncentiveDrawer;
  const incentiveQuery = useIncentiveQuery(id, { enabled: !isRebate });
  const rebateQuery = useRebateQuery(id, { enabled: isRebate });
  const {
    data: promotion,
    error: promotionError,
    isLoading: isPromotionLoading,
  } = isRebate ? rebateQuery : incentiveQuery;

  const productsSearch = { page_size: MAX_PRODUCTS, page: 1 };
  const incentiveProductsQuery = useIncentiveProductsQuery(id, productsSearch, {
    enabled: shouldDisplayItems && !isRebate,
  });
  const rebateProductsQuery = useRebateProductsQuery(id, productsSearch, {
    enabled: shouldDisplayItems && isRebate,
  });
  const {
    data: products,
    error: productsError,
    isLoading: areProductsLoading,
  } = isRebate ? rebateProductsQuery : incentiveProductsQuery;
  const itemListRef = useViewItemListOnVisible(products?.results, listSource);
  const productsViewRef = useProductsViewOnVisible(products?.results, listSource, {
    promotionId: id,
    numResults: products?.paginator.count,
  });

  const renderProductsContent = () => {
    if (areProductsLoading) {
      return <LoadingContainer />;
    }

    // A promotion opened from a past order may have ended: its details still load but its
    // products 404, so the drawer shows the offer and terms without the product list.
    if (productsError?.status === StatusCode.NotFound) {
      return null;
    }

    if (productsError) {
      return <FetchErrorContainer error={productsError} entityName="product" isPlural={true} />;
    }

    if (!products?.results.length) {
      return null;
    }

    const handleNavigate = () => {
      close();
      onNavigate?.();
    };

    const Card = isPhoneAndSmaller ? ProductCardMobile : ProductCard;

    return (
      <div ref={itemListRef}>
        <div className={styles.productsLabel}>Applicable products:</div>
        <div
          ref={productsViewRef}
          className={isPhoneAndSmaller ? styles.productsList : styles.productsGrid}
        >
          {products.results.map((result) => (
            <Card
              key={result.id}
              result={result}
              listSource={listSource}
              onNavigate={handleNavigate}
            />
          ))}
        </div>
        {products.paginator.count > MAX_PRODUCTS && (
          <LinkButton
            className={styles.link}
            dest={
              isRebate
                ? urls.products.rebate({ params: { id } })
                : urls.products.incentive({ params: { id } })
            }
            onClick={handleNavigate}
          >
            <div className={styles.linkContent}>
              See all products for this promotion
              <Icon className={styles.arrow} icon={faArrowRight} fixedWidth={true} />
            </div>
          </LinkButton>
        )}
      </div>
    );
  };

  if (isPromotionLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!promotion || promotionError) {
    return (
      <DrawerLayout.FetchError
        error={promotionError}
        entityName={isRebate ? "rebate" : "incentive"}
      />
    );
  }

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title={isRebate ? "Rebate offer details" : "Incentive offer details"}
        onClose={close}
      />
      <DrawerLayout.Content>
        {/* A global autoship incentive carries no title or message. */}
        {!!(promotion.promo_title && promotion.promo_message) && (
          <Banner variant="promotion">
            <Banner.Content label={promotion.promo_title} description={promotion.promo_message} />
          </Banner>
        )}
        {shouldDisplayItems && renderProductsContent()}
        {promotion.promo_details && <Markdown markdown={promotion.promo_details} />}
      </DrawerLayout.Content>
    </DrawerLayout>
  );
};

export default PromotionDrawer;
